import { toError } from '@main/shared/utils';

export interface IntegrationErrorOptions extends ErrorOptions {
  status?: IntegrationErrorStatus;
  message?: string;
  internalMessage?: string;
}

export enum IntegrationErrorStatus {
  Unauthenticated = 'UNAUTHENTICATED',
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
  InvalidInput = 'INVALID_INPUT',
}

const ErrorStatusToHttpStatus: Record<IntegrationErrorStatus, number> = {
  [IntegrationErrorStatus.InvalidInput]: 400,
  [IntegrationErrorStatus.Unauthenticated]: 401,
  [IntegrationErrorStatus.Forbidden]: 403,
  [IntegrationErrorStatus.NotFound]: 404,
};

export class IntegrationError extends Error {
  /**
   * Creates an instance of `IntegrationError` from an existing `Error` object.
   *
   * @param {Error} error - The original error to wrap inside the `IntegrationError`.
   * @param {IntegrationErrorOptions} [options] - Optional settings for the new error.
   *
   * @returns {IntegrationError} A new instance of `IntegrationError` containing the provided or
   * derived public message and the original error as the cause.
   */
  static fromError(error: Error, options?: IntegrationErrorOptions): IntegrationError {
    const publicMessage = options?.message ?? error.message;
    const internalMessage = options?.internalMessage ?? error.message;

    return new IntegrationError(publicMessage, {
      ...options,
      cause: error,
      internalMessage,
    });
  }

  static unauthenticated = fromStatusCreator(
    IntegrationErrorStatus.Unauthenticated,
    'The provided credentials are invalid.',
  );

  static forbidden = fromStatusCreator(
    IntegrationErrorStatus.Forbidden,
    'The provided credentials lack the necessary permissions required for this integration.',
  );

  static notFound = fromStatusCreator(
    IntegrationErrorStatus.NotFound,
    'The requested resource was not found.',
  );

  static invalidInput = fromStatusCreator(
    IntegrationErrorStatus.InvalidInput,
    "The request can't be processed due to invalid syntax or missing information. Please check your input and try again.",
  );

  readonly status?: IntegrationErrorStatus;
  readonly internalMessage?: string;

  constructor(message?: string, options?: IntegrationErrorOptions) {
    super(message, options);

    this.status = options?.status;
    this.internalMessage = options?.internalMessage;
  }

  getErrorCause() {
    return this.cause instanceof Error ? this.cause : undefined;
  }

  getHttpStatus(): number {
    // Internal errors should not be surfaced to the user via IntegrationError, so
    // defaulting to 400 instead of 500, as it is the more commonly encountered case.
    return ErrorStatusToHttpStatus[this.status ?? IntegrationErrorStatus.InvalidInput];
  }
}

function fromStatusCreator(status: IntegrationErrorStatus, defaultMessage: string) {
  return (options?: IntegrationErrorOptions) => {
    const message = options?.message ?? defaultMessage;
    const finalOptions = { ...options, status, message };
    return options?.cause
      ? IntegrationError.fromError(toError(options.cause), finalOptions)
      : new IntegrationError(message, finalOptions);
  };
}
